import React, { useState } from "react";
import { toast } from "react-toastify";
import StyledDialog from "../StyledDialog";
import {
    addOnlineLectureHomeWork,
    updateOnlineLectureHomeWork,
} from "../../../../api/onlineLecture";
import { HOMEWORK_TYPE_OPTIONS, QUESTION_COUNT_MIN, QUESTION_COUNT_MAX } from "../../constants";

// Homework add/update modal. Preserved legacy rules:
//  - only PDF and "Serbest Soru Çözümü" are selectable; the other three types
//    stay visible but disabled
//  - QuestionCount is live-clamped to 1–120
//  - in update mode the type is inferred from QuestionCount > 0 (not editable)
const HomeworkModal = ({ open, onClose, lecture, homework, isUpdate, adminToken, onSaved }) => {
    const derivedType = isUpdate
        ? homework.QuestionCount > 0
            ? "Serbest Soru Çözümü"
            : "PDF"
        : "";
    const [selectedType, setSelectedType] = useState(derivedType);
    const [printableTestCode, setPrintableTestCode] = useState(isUpdate ? homework.PrintableTestCode || "" : "");
    const [questionCount, setQuestionCount] = useState(isUpdate && homework.QuestionCount > 0 ? homework.QuestionCount : "");
    const [title, setTitle] = useState(isUpdate ? homework.Title || "" : "");
    const [description, setDescription] = useState(isUpdate ? homework.Description || "" : "");
    const [orderId, setOrderId] = useState(isUpdate ? homework.OrderId : "");

    // In update mode the select's value is derived from the data, like legacy
    const typeValue = isUpdate ? derivedType : selectedType;
    const showPdfField = isUpdate ? homework.QuestionCount == 0 : typeValue === "PDF";
    const showCountField = isUpdate ? homework.QuestionCount > 0 : typeValue === "Serbest Soru Çözümü";
    const showCommonFields = isUpdate || Boolean(typeValue);

    const clampQuestionCount = (raw) => {
        const value = parseInt(raw);
        if (value < QUESTION_COUNT_MIN || isNaN(value)) {
            setQuestionCount("");
        } else if (value > QUESTION_COUNT_MAX) {
            setQuestionCount(QUESTION_COUNT_MAX);
        } else {
            setQuestionCount(value);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const formJson = {};
        if (showPdfField) formJson.PrintableTestCode = printableTestCode;
        if (showCountField) {
            formJson.QuestionCount = questionCount;
            if (formJson.QuestionCount) formJson.QuestionCount = parseInt(formJson.QuestionCount);
        }
        if (showCommonFields) {
            formJson.Title = title;
            formJson.Description = description;
        }
        formJson.OrderId = parseInt(orderId);
        formJson.OnlineLectureId = lecture.Id;
        formJson.SubCourseTopicUniqueId = lecture.SubCourseTopicUniqueId;
        formJson.ChannelCode = "Web";
        if (isUpdate) {
            formJson.Id = homework.Id;
            formJson.SubCourseTopicVideoId = homework.SubCourseTopicVideoId;
        }
        formJson.SubCourseTopicVideoUrl = "";

        const res = await (isUpdate
            ? updateOnlineLectureHomeWork(formJson, adminToken)
            : addOnlineLectureHomeWork(formJson, adminToken));
        if (res.status == 200) {
            toast.success(isUpdate ? "ders başarıyla güncellenmiştir" : "ders başarıyla eklenmiştir");
            onSaved();
        } else {
            toast.error("bir sorun oluştu lütfen tekrar deneyiniz");
        }
        onClose();
    };

    return (
        <StyledDialog open={open} onClose={onClose}>
            <div className="olp-theme olp-modal">
                <h2 className="olp-modal-title">{isUpdate ? "Ödev Güncelle" : "Ödev Ekle"}</h2>
                <div className="olp-modal-readonly" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <img
                        style={{ height: 60, width: 80, objectFit: "cover", borderRadius: 6 }}
                        src={lecture.ThumbnailUrl}
                        alt=""
                    />
                    <b>{lecture.SubTopicName}</b>
                </div>
                <form method="post" onSubmit={handleSubmit}>
                    <div className="olp-field">
                        <label htmlFor="options">Lütfen Ödev Türü Seçiniz:</label>
                        <select
                            className="olp-select"
                            id="options"
                            value={typeValue}
                            onChange={(e) => setSelectedType(e.target.value)}
                        >
                            {HOMEWORK_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value} disabled={option.disabled}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {showPdfField && (
                        <div className="olp-field">
                            <label htmlFor="pdfInput">PDF Kodu Giriniz:</label>
                            <input
                                className="olp-input"
                                name="PrintableTestCode"
                                type="text"
                                id="pdfInput"
                                value={printableTestCode}
                                onChange={(e) => setPrintableTestCode(e.target.value)}
                            />
                        </div>
                    )}
                    {showCountField && (
                        <div className="olp-field">
                            <label htmlFor="SerbestSoruSayısı">Serbest Soru Sayısını Giriniz:</label>
                            <input
                                className="olp-input"
                                type="number"
                                max={QUESTION_COUNT_MAX}
                                min={QUESTION_COUNT_MIN}
                                id="SerbestSoruSayısı"
                                name="QuestionCount"
                                value={questionCount}
                                onChange={(e) => clampQuestionCount(e.target.value)}
                            />
                        </div>
                    )}
                    {showCommonFields && (
                        <React.Fragment>
                            <div className="olp-field">
                                <label htmlFor="title">Başlık:</label>
                                <input
                                    className="olp-input"
                                    type="text"
                                    id="title"
                                    name="Title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="description">Açıklama:</label>
                                <input
                                    className="olp-input"
                                    type="text"
                                    id="description"
                                    name="Description"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                />
                            </div>
                            <div className="olp-field">
                                <label htmlFor="order">Ödev sıralaması:</label>
                                <input
                                    className="olp-input"
                                    type="number"
                                    id="order"
                                    name="OrderId"
                                    value={orderId}
                                    onChange={(e) => setOrderId(e.target.value)}
                                />
                            </div>
                        </React.Fragment>
                    )}
                    <div className="olp-modal-footer">
                        <button type="button" className="olp-btn" onClick={onClose}>
                            Vazgeç
                        </button>
                        <button type="submit" className="olp-btn olp-btn--primary">
                            {isUpdate ? "Ödev Güncelle" : "Ödev Ekle"}
                        </button>
                    </div>
                </form>
            </div>
        </StyledDialog>
    );
};

export default HomeworkModal;
